// src/pages/InfoPage.js
// Public marketing / info page — accessible to anyone at /info (no auth required).
// Explains what GAAFrfid does, shows a demo video, and lists pricing.
import React from 'react';
import { Link } from 'react-router-dom';
import {
    Boxes, ScanLine, Warehouse, Bell, Bot, Users,
    Clock, ShieldCheck, Smartphone, BarChart3, ArrowRight,
    Check, PlayCircle, Mail,
} from 'lucide-react';
import styles from './InfoPage.module.css';

// ── Feature grid ────────────────────────────────────────────────────────────
const FEATURES = [
    {
        icon: ScanLine,
        title: 'Real-Time RFID Tracking',
        desc: 'Every tagged item is read automatically as it moves. No barcodes to scan one by one — count an entire pallet in seconds.',
    },
    {
        icon: Boxes,
        title: 'Live Inventory',
        desc: 'Always know exactly what you have and where. Stock levels update the moment a tag is read at any reader or gate.',
    },
    {
        icon: Warehouse,
        title: 'Multi-Warehouse',
        desc: 'Manage one site or dozens from a single dashboard. See stock per warehouse and move items between locations.',
    },
    {
        icon: Bell,
        title: 'Smart Alerts',
        desc: 'Get notified instantly on low stock, unexpected movement, or items leaving without authorization.',
    },
    {
        icon: Bot,
        title: 'AI Assistant',
        desc: 'Ask questions about your inventory in plain language and get instant answers, summaries, and insights.',
    },
    {
        icon: Users,
        title: 'Staff Attendance',
        desc: 'RFID badges log staff clock-in and clock-out automatically — accurate attendance with zero manual entry.',
    },
    {
        icon: BarChart3,
        title: 'Reports & Analytics',
        desc: 'Visual dashboards on stock trends, movement history, and usage so you can make decisions with confidence.',
    },
    {
        icon: Smartphone,
        title: 'Dashboard + Mobile App',
        desc: 'Full web dashboard for the office and a companion mobile app for the floor — your team stays in sync everywhere.',
    },
];

// Google Ads "Submit lead form" click conversion. The base gtag is loaded in
// index.html; we fire the event when a visitor clicks a "Get started" CTA
// (which opens the sales email). Guarded so it never breaks the click if gtag
// hasn't loaded (e.g. blocked by an ad blocker).
const ADS_LEAD_SEND_TO = 'AW-16776229518/W4AkCO_m1O8ZEI7lw78-'
function trackLeadConversion() {
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', { send_to: ADS_LEAD_SEND_TO })
        }
    } catch { /* never block the CTA */ }
}

// ── Pricing (kept in sync with BillingPage.js PLANS) ──────────────────────────
const PLANS = [
    {
        key: 'starter',
        name: 'Starter',
        price: 399,
        blurb: 'For a single site getting started with RFID.',
        features: [
            '1 Warehouse',
            '500 tag events / month',
            '50K AI tokens / month',
            'Dashboard + Mobile App',
            'RFID Staff Attendance',
            'Email Alerts',
        ],
        highlight: false,
    },
    {
        key: 'growth',
        name: 'Growth',
        price: 799,
        blurb: 'For growing operations across multiple locations.',
        features: [
            'Up to 3 Warehouses',
            '2,000 tag events / month',
            '200K AI tokens / month',
            'Dashboard + Mobile App',
            'RFID Staff Attendance',
            'Email Alerts',
        ],
        highlight: true,
    },
    {
        key: 'enterprise',
        name: 'Enterprise',
        price: 1499,
        blurb: 'For large operations that need it all.',
        features: [
            'Unlimited Warehouses',
            'Unlimited tag events',
            '1M AI tokens / month',
            'Dashboard + Mobile App',
            'RFID Staff Attendance',
            'Email Alerts',
        ],
        highlight: false,
    },
];

export default function InfoPage() {
    const year = new Date().getFullYear();

    return (
        <div className={styles.page}>
            {/* ── Background decoration ── */}
            <div className={styles.bg} aria-hidden="true">
                <div className={styles.bgGlow} />
                <div className={styles.bgGrid} />
            </div>

            {/* ── Top nav ── */}
            <header className={styles.nav}>
                <div className={styles.logo}>
                    <div className={styles.logoMark}><span>G</span></div>
                    <span className={styles.logoName}>GAAF<em>rfid</em></span>
                </div>
                <nav className={styles.navLinks}>
                    <a href="#features">Features</a>
                    <a href="#demo">Demo</a>
                    <a href="#pricing">Pricing</a>
                    <Link to="/login" className={styles.navCta}>Sign In <ArrowRight size={15} /></Link>
                </nav>
            </header>

            <main className={styles.main}>
                {/* ── Hero ── */}
                <section className={styles.hero}>
                    <div className={styles.eyebrow}>RFID Inventory Management Platform</div>
                    <h1 className={styles.heroTitle}>
                        Know exactly what you have,<br />
                        <em>where it is</em>, in real time.
                    </h1>
                    <p className={styles.heroSub}>
                        GAAFrfid turns RFID tags into live inventory intelligence. Track stock across every
                        warehouse, automate staff attendance, get instant alerts, and ask an AI assistant
                        anything about your operation — all from one dashboard.
                    </p>
                    <div className={styles.heroActions}>
                        <a href="#demo" className={styles.primaryBtn}>
                            <PlayCircle size={18} /> Watch the 3-min demo
                        </a>
                        <a href="#pricing" className={styles.secondaryBtn}>
                            See pricing <ArrowRight size={16} />
                        </a>
                    </div>
                    <div className={styles.trustRow}>
                        <span><ShieldCheck size={15} /> Secure &amp; cloud-hosted</span>
                        <span><Clock size={15} /> Live updates</span>
                        <span><Smartphone size={15} /> Web + mobile</span>
                    </div>
                </section>

                {/* ── Demo video ── */}
                <section id="demo" className={styles.demoSection}>
                    <div className={styles.sectionHead}>
                        <h2>See it in action</h2>
                        <p>RFID Equipment</p>
                    </div>

                    <div className={styles.videoFrame}>
                        {/*
                          ── HOW TO ADD YOUR DEMO VIDEO ──────────────────────────────
                          Drop your video file into the dashboard's `public/` folder,
                          e.g.  public/demo.mp4   (and optionally a poster image
                          public/demo-poster.jpg). Then it will play below automatically.

                          • Recommended: MP4 (H.264) at 1080p, ~3 minutes.
                          • To use a different filename, change the <source> src below.
                          • To embed a YouTube/Vimeo link instead, replace the <video>
                            block with an <iframe> pointing at your video URL.
                        */}
                        <video
                            className={styles.video}
                            controls
                            preload="metadata"
                            poster="/GAAFicon.png"
                        >
                            <source src="/equipment.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </section>

                <section id="demo" className={styles.demoSection}>
                    <div className={styles.sectionHead}>
                        <p>A 3-minute walkthrough of how GAAFrfid works, from tag to dashboard.</p>
                    </div>

                    <div className={styles.videoFrame}>
                        {/*
                          ── HOW TO ADD YOUR DEMO VIDEO ──────────────────────────────
                          Drop your video file into the dashboard's `public/` folder,
                          e.g.  public/demo.mp4   (and optionally a poster image
                          public/demo-poster.jpg). Then it will play below automatically.

                          • Recommended: MP4 (H.264) at 1080p, ~3 minutes.
                          • To use a different filename, change the <source> src below.
                          • To embed a YouTube/Vimeo link instead, replace the <video>
                            block with an <iframe> pointing at your video URL.
                        */}
                        <video
                            className={styles.video}
                            controls
                            preload="metadata"
                            poster="/GAAFicon.png"
                        >
                            <source src="/demo.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </section>

                {/* ── Features ── */}
                <section id="features" className={styles.featuresSection}>
                    <div className={styles.sectionHead}>
                        <h2>Everything you need to run inventory with RFID</h2>
                        <p>One platform replaces spreadsheets, manual counts, and guesswork.</p>
                    </div>
                    <div className={styles.featureGrid}>
                        {FEATURES.map(({ icon: Icon, title, desc }) => (
                            <div key={title} className={styles.featureCard}>
                                <div className={styles.featureIcon}><Icon size={20} /></div>
                                <h3>{title}</h3>
                                <p>{desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── How it works ── */}
                <section className={styles.howSection}>
                    <div className={styles.sectionHead}>
                        <h2>How it works</h2>
                        <p>From tagging your first item to live insights in three simple steps.</p>
                    </div>
                    <div className={styles.steps}>
                        <div className={styles.step}>
                            <div className={styles.stepNum}>1</div>
                            <h3>Tag your items</h3>
                            <p>Attach an RFID tag to each product, asset, or staff badge. Each tag carries a unique ID.</p>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNum}>2</div>
                            <h3>Readers do the counting</h3>
                            <p>RFID readers at gates and shelves pick up tags automatically and stream events to the cloud.</p>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNum}>3</div>
                            <h3>You get the picture</h3>
                            <p>Your dashboard updates live — stock levels, movement, attendance, and alerts, all in one place.</p>
                        </div>
                    </div>
                </section>

                {/* ── Pricing ── */}
                <section id="pricing" className={styles.pricingSection}>
                    <div className={styles.sectionHead}>
                        <h2>Simple, transparent pricing</h2>
                        <p>Pick the plan that fits your operation. Upgrade or downgrade anytime.</p>
                    </div>
                    <div className={styles.plansGrid}>
                        {PLANS.map(plan => (
                            <div
                                key={plan.key}
                                className={`${styles.planCard} ${plan.highlight ? styles.planCardHighlight : ''}`}
                            >
                                {plan.highlight && <div className={styles.planBadge}>Most Popular</div>}
                                <div className={styles.planName}>{plan.name}</div>
                                <p className={styles.planBlurb}>{plan.blurb}</p>
                                <div className={styles.planPrice}>
                                    <span className={styles.planAmount}>${plan.price}</span>
                                    <span className={styles.planPer}>/mo</span>
                                </div>
                                <ul className={styles.planFeatures}>
                                    {plan.features.map(f => (
                                        <li key={f}><Check size={15} /> {f}</li>
                                    ))}
                                </ul>
                                <a
                                    href="mailto:rfidsales@gaaftechnologies.com"
                                    onClick={trackLeadConversion}
                                    className={plan.highlight ? styles.planBtnPrimary : styles.planBtnSecondary}
                                >
                                    Get started
                                </a>
                            </div>
                        ))}
                    </div>
                    <p className={styles.wireNote}>
                        Prefer to pay by wire transfer or check, or need a custom plan? Contact us at{' '}
                        <a href="mailto:rfidsales@gaaftechnologies.com" onClick={trackLeadConversion}>rfidsales@gaaftechnologies.com</a> —
                        we'll set you up manually.
                    </p>
                </section>

                {/* ── Final CTA ── */}
                <section className={styles.ctaSection}>
                    <div className={styles.ctaCard}>
                        <h2>Ready to see your inventory in real time?</h2>
                        <p>Get in touch and we'll get your warehouse live on GAAFrfid.</p>
                        <div className={styles.ctaActions}>
                            <a href="mailto:rfidsales@gaaftechnologies.com" className={styles.primaryBtn} onClick={trackLeadConversion}>
                                <Mail size={17} /> Contact Sales
                            </a>
                            <Link to="/login" className={styles.secondaryBtn}>
                                Sign In <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            {/* ── Footer ── */}
            <footer className={styles.footer}>
                <div className={styles.footerLogo}>
                    <div className={styles.logoMark}><span>G</span></div>
                    <span className={styles.logoName}>GAAF<em>rfid</em></span>
                </div>
                <p>RFID Inventory Management Platform by GAAF Technologies.</p>
                <p className={styles.copyright}>© {year} GAAF Technologies · rfidsales@gaaftechnologies.com</p>
            </footer>
        </div>
    );
}
